import { API_BASE } from "./config"

// ---------------------------------------------------------------------------
// Token storage — in memory only (XSS protection, never localStorage)
// ---------------------------------------------------------------------------
let accessToken: string | null = null
let refreshToken: string | null = null
let refreshTimer: ReturnType<typeof setTimeout> | null = null

// Dedup guards — prevent concurrent token fetches
let sessionPromise: Promise<boolean> | null = null
let refreshPromise: Promise<boolean> | null = null

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function setTokens(access: string, refresh: string, expiresIn: number) {
  accessToken = access
  refreshToken = refresh
  persistDevPair(access, refresh)
  scheduleProactiveRefresh(expiresIn)
}

function scheduleProactiveRefresh(expiresIn: number) {
  if (refreshTimer) clearTimeout(refreshTimer)
  // Refresh 60 seconds before expiry to avoid 401s mid-session
  const delay = Math.max((expiresIn - 60) * 1000, 0)
  refreshTimer = setTimeout(async () => {
    const ok = await tryRefresh()
    if (!ok) await trySessionExchange()
  }, delay)
}

// ---------------------------------------------------------------------------
// Session cookie → JWT exchange
// POST /jwt-token — same-site route, cookie sent automatically
//   travelhub.by/jwt-token   (on travelhub)
//   travel-code.com/jwt-token (on travelcode)
// ---------------------------------------------------------------------------

async function doSessionExchange(): Promise<boolean> {
  try {
    const res = await fetch("/jwt-token", {
      method: "POST",
      credentials: "include",
    })
    if (!res.ok) return false
    const data = await res.json()
    setTokens(data.accessToken, data.refreshToken, data.expiresIn)
    return true
  } catch {
    return false
  }
}

/**
 * Dev only: the harness seeds the pair from localStorage on each load, and the
 * refresh token rotates on every use — so a reload after a refresh would replay
 * a burnt token and land on the error page. Write the rotated pair back where
 * the harness reads it. Production never has these keys, so this is a no-op there.
 */
function persistDevPair(access: string, refresh: string): void {
  if (process.env.NODE_ENV !== "development" || typeof window === "undefined") return
  try {
    if (window.localStorage?.getItem("robert:devRefresh")) {
      window.localStorage.setItem("robert:devToken", access)
      window.localStorage.setItem("robert:devRefresh", refresh)
    }
  } catch { /* storage unavailable */ }
}

function trySessionExchange(): Promise<boolean> {
  if (!sessionPromise) {
    sessionPromise = doSessionExchange().finally(() => {
      sessionPromise = null
    })
  }
  return sessionPromise
}

// ---------------------------------------------------------------------------
// Refresh token rotation
// POST /v1/auth/jwt/refresh — on api.travel-code.com (no cookie needed)
// ---------------------------------------------------------------------------

async function doRefresh(): Promise<boolean> {
  if (!refreshToken) return false
  try {
    const res = await fetch(`${API_BASE}/auth/jwt/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    })
    if (!res.ok) {
      refreshToken = null
      return false
    }
    const data = await res.json()
    setTokens(data.accessToken, data.refreshToken, data.expiresIn)
    return true
  } catch {
    return false
  }
}

function tryRefresh(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Returns current access token, or fetches one via session cookie exchange. */
export async function ensureAccessToken(): Promise<string | null> {
  if (accessToken) return accessToken
  const ok = await trySessionExchange()
  return ok ? accessToken : null
}

/**
 * Called on 401: invalidates current token and tries to recover.
 * Order: refresh token → session cookie → null (session dead).
 */
export async function handleUnauthorized(): Promise<string | null> {
  accessToken = null

  if (refreshToken) {
    const ok = await tryRefresh()
    if (ok) return accessToken
  }

  const ok = await trySessionExchange()
  return ok ? accessToken : null
}

/** Clear all tokens (logout / session death). */
export function clearTokens() {
  accessToken = null
  refreshToken = null
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
}

/**
 * Dev only: seed the in-memory pair from the review harness so the page keeps
 * itself signed in. `?devToken=` alone dies after 15 minutes and the reviewer
 * gets bounced to an error mid-look; with the refresh token the proactive timer
 * rotates the pair for as long as the refresh lives (7 days on the platform).
 * Inert in production builds — the harness never sets these there.
 */
export function seedDevTokens(access: string, refresh: string | null, expiresIn = 900): void {
  if (process.env.NODE_ENV !== "development") return
  if (!access) return
  if (refresh) setTokens(access, refresh, expiresIn)
  else accessToken = access
}
